import { useState, useEffect, useRef } from 'react';

const getScrollParent = (element) => {
  let parent = element.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

const FormSection = ({ id, icon: Icon, title, children, headerRight, className, ref }) => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const localRef = useRef(null);

  const setRef = (node) => {
    localRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  useEffect(() => {
    const node = localRef.current;
    if (!node) return;

    const scrollParent = getScrollParent(node);

    // Fallback: ensure section becomes visible after 2 seconds regardless
    const fallbackTimer = setTimeout(() => {
      setHasAnimated(true);
    }, 2000);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasAnimated(true);
          clearTimeout(fallbackTimer);
          observer.disconnect();
        }
      },
      { threshold: 0.1, root: scrollParent }
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, []);

  return (
    <div
      ref={setRef}
      id={id}
      className={`scroll-mt-6 transition-opacity duration-500 ease-out
        ${hasAnimated ? 'opacity-100' : 'opacity-0'}
        ${className || ''}`}
    >
      {(Icon || title || headerRight) && (
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex min-w-0 items-center gap-2.5">
            {Icon && (
              <div className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/20">
                <Icon className="h-4.5 w-4.5 text-primary-600 dark:text-primary-400" />
              </div>
            )}
            {title && (
              <h3 className="min-w-0 break-words text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
                {title}
              </h3>
            )}
          </div>
          {headerRight && <div className="flex min-w-0 flex-wrap items-center gap-2">{headerRight}</div>}
        </div>
      )}
      {children}
    </div>
  );
};

export default FormSection;
